/**
 * @fileoverview Standalone schema.org node builders — pure functions, descriptor in, plain object out.
 *
 * The ONE home for the Organization / WebSite / WebPage / Product / BreadcrumbList shapes every
 * ParcelPuffin surface emits (LIFE-SHORTLISTED-001 P1.5). Before this module the same node shapes lived
 * in three places: `@puffin-core/storefront-cms` (tenant storefront + purchase-web's product page),
 * `@puffin-core/marketing-shell`'s blog builders, and this package's `buildJsonLd` graph. Each copy
 * could drift from the others with nothing to notice.
 *
 * Two forms per node:
 *   - `organizationNode(...)` and friends are GRAPH MEMBERS: no `@context`, optional stable `@id`,
 *     for composition inside an `@graph` (see `buildJsonLd`).
 *   - `build*JsonLd(...)` are STANDALONE documents: the same node with the schema.org `@context`,
 *     for a single `<script type="application/ld+json">` (the tenant storefront's head).
 *
 * Every builder omits absent fields — no empty or `undefined` keys — because a search engine that
 * fails to parse a block discards the page's structured data entirely, silently.
 *
 * Browser-safe by construction (no Node built-ins): the package is dual-use through a GENERATED ESM
 * re-export shim (`tooling/esm-build`), never a hand-written mirror.
 *
 * @module @puffin-core/seo/schemaOrgNodes
 */

'use strict';

const { readableAmount } = require('@puffin-core/money/format-usd');

const SCHEMA_CONTEXT = 'https://schema.org';
const IN_STOCK = 'https://schema.org/InStock';
const OUT_OF_STOCK = 'https://schema.org/OutOfStock';
const DEFAULT_CURRENCY = 'USD';

/**
 * A schema.org `Date` or `DateTime` as this package accepts it: a civil `YYYY-MM-DD`, or an ISO
 * instant `YYYY-MM-DDTHH:mm[:ss[.fff]](Z|±HH:MM)`. The captured day must EXIST — `new Date` would
 * slide `2026-02-30` to March 2 (TS-001's overflow), and a page stamped with a day that never
 * happened is a claim an engine cannot trust. The round-trip below is `@puffin-core/temporal-core`'s
 * `isCivilDate` canon, written here because this module requires only `@puffin-core/money`'s format leaf (the
 * `./schema-org` subpath is a browser bundle's entry, and its whole require closure is pinned).
 */
const SCHEMA_DATE_SHAPE =
  /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2}))?$/;

/**
 * @param {*} value
 * @returns {boolean} Whether `value` is a schema.org date this package will stamp on a node.
 */
function isSchemaDate(value) {
  if (typeof value !== 'string') return false;
  const match = SCHEMA_DATE_SHAPE.exec(value);
  if (!match) return false;
  const [, year, month, day] = match;
  const utcDay = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return utcDay.toISOString().slice(0, 10) === `${year}-${month}-${day}`;
}

/**
 * @param {string} builder - The builder refusing, for the message.
 * @param {*} value - A `dateModified`.
 * @returns {?string} `value` when it is a schema.org date; `undefined` when absent.
 * @throws {TypeError} When present and not a date — a wrong stamp is never omitted silently.
 */
function schemaDateOrAbsent(builder, value) {
  if (value === null || value === undefined) return undefined;
  if (!isSchemaDate(value)) {
    throw new TypeError(
      `${builder}: dateModified must be a civil YYYY-MM-DD date or an ISO instant naming a real day, received ${JSON.stringify(value)}`,
    );
  }
  return value;
}

/**
 * @param {string} builder
 * @param {Object} opts
 * @param {string[]} fields - The fields the node cannot be published without.
 * @throws {TypeError} Naming the first missing field.
 */
function requireStrings(builder, opts, fields) {
  for (const field of fields) {
    if (typeof opts[field] !== 'string' || opts[field].length === 0) {
      throw new TypeError(`${builder}: ${field} (a non-empty string) is required`);
    }
  }
}

/**
 * Join an origin and a path into an absolute URL. Tolerant of a missing origin (returns the path)
 * and of leading/trailing slashes.
 *
 * @param {?string} origin - e.g. `https://customcarvers.com`
 * @param {?string} path - e.g. `/products/prd_1` or `products/prd_1`
 * @returns {string}
 */
function absoluteUrl(origin, path) {
  const safePath = path === null || path === undefined ? '' : String(path);
  if (!origin) {
    return safePath.startsWith('/') ? safePath : `/${safePath}`;
  }
  const base = origin.replace(/\/+$/, '');
  if (!safePath || safePath === '/') {
    return `${base}/`;
  }
  return `${base}/${safePath.replace(/^\/+/, '')}`;
}

/**
 * Resolve an asset URL (logo / product image / share card) to an absolute URL. Already-absolute
 * (`http(s)://…`) values pass through unchanged; relative values are joined to the origin. Empty
 * values resolve to `undefined`.
 *
 * @param {?string} origin
 * @param {?string} value
 * @returns {?string}
 */
function resolveAssetUrl(origin, value) {
  if (!value) {
    return undefined;
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  return absoluteUrl(origin, value);
}

/**
 * Prefix a graph-member node with the schema.org `@context`, making it a standalone document.
 *
 * @param {Object} node
 * @returns {Object}
 */
function withSchemaContext(node) {
  return { '@context': SCHEMA_CONTEXT, ...node };
}

/**
 * Organization — graph member (no `@context`).
 *
 * Never a `Person`, and never carries `founder` / `author`: an owner-blind tenant must not be
 * resolvable to an individual, so this builder has no field that could name one.
 *
 * @param {Object} [opts]
 * @param {string} [opts.id]        stable `@id`, e.g. `https://x.com/#organization`
 * @param {string} opts.name        display / brand name
 * @param {string} [opts.legalName] the operating legal entity, when it differs from the brand
 * @param {string} opts.url         absolute home URL
 * @param {string} [opts.logo]      absolute logo URL
 * @param {string[]} [opts.sameAs]  public profile URLs — emitted only when non-empty
 * @param {string} [opts.email]     public contact address
 * @returns {Object}
 */
function organizationNode({ id, name, legalName, url, logo, sameAs, email } = {}) {
  const node = { '@type': 'Organization' };
  if (id) node['@id'] = id;
  node.name = name;
  if (legalName) node.legalName = legalName;
  node.url = url;
  if (logo) node.logo = logo;
  // An empty `sameAs` is noise, and a FABRICATED one is a false claim about identity — the single
  // worst thing to get wrong in structured data. Copied, never aliased.
  if (Array.isArray(sameAs) && sameAs.length > 0) node.sameAs = sameAs.slice();
  if (email) node.email = email;
  return node;
}

/**
 * Organization — standalone document.
 *
 * @param {Object} [opts] see {@link organizationNode}
 * @returns {Object}
 */
function buildOrganizationJsonLd(opts = {}) {
  return withSchemaContext(organizationNode(opts));
}

/**
 * WebSite — standalone document.
 *
 * @param {Object} [opts]
 * @param {string} opts.name
 * @param {string} opts.url
 * @returns {Object}
 */
function buildWebSiteJsonLd({ name, url } = {}) {
  return withSchemaContext({ '@type': 'WebSite', name, url });
}

/**
 * WebPage — standalone document.
 *
 * @param {Object} [opts]
 * @param {string} opts.name
 * @param {string} opts.url
 * @param {?string} [opts.description]
 * @param {?string} [opts.inLanguage]
 * @param {?string} [opts.dateModified] when the page's content was last verified or changed
 *   (LINKABLE-ASSETS-001 C3: a reference page stamps its `lastVerifiedAt`); a civil date or an
 *   ISO instant naming a real day — refused otherwise, omitted when absent
 * @returns {Object}
 * @throws {TypeError} When `dateModified` is given and is no date.
 */
function buildWebPageJsonLd({ name, url, description, inLanguage, dateModified } = {}) {
  const node = { '@type': 'WebPage', name, url };
  if (description) node.description = description;
  if (inLanguage) node.inLanguage = inLanguage;
  const stamp = schemaDateOrAbsent('buildWebPageJsonLd', dateModified);
  if (stamp) node.dateModified = stamp;
  return withSchemaContext(node);
}

/**
 * Dataset — standalone document (LINKABLE-ASSETS-001 C3): a public reference table (a rate guide,
 * a per-jurisdiction rule table) as the thing an engine can cite. `@id` is `${url}#dataset`; the
 * creator is the site's Organization by `@id` (the `buildJsonLd` graph convention, `${site}#organization`).
 *
 * @param {Object} opts
 * @param {string} opts.name
 * @param {string} opts.description
 * @param {string} opts.url absolute page URL
 * @param {string} opts.creatorId the site Organization's `@id`
 * @param {string} opts.dateModified a civil date or an ISO instant (the table's `lastVerifiedAt`)
 * @param {?string} [opts.license] an absolute license URL
 * @param {?string} [opts.temporalCoverage] ISO 8601 interval, e.g. `2026-01-01/2026-12-31`
 * @param {?Array<{contentUrl: string, encodingFormat: string}>} [opts.distribution] downloadable
 *   forms of the data, each a `DataDownload`
 * @param {?string[]} [opts.keywords] emitted only when non-empty; copied, never aliased
 * @returns {Object}
 * @throws {TypeError} When a required field is missing, `dateModified` is no date, or a
 *   distribution entry lacks its `contentUrl` / `encodingFormat`.
 */
function buildDatasetJsonLd(opts = {}) {
  requireStrings('buildDatasetJsonLd', opts, ['name', 'description', 'url', 'creatorId']);
  const { name, description, url, creatorId, license, temporalCoverage, distribution, keywords } =
    opts;
  const stamp = schemaDateOrAbsent('buildDatasetJsonLd', opts.dateModified);
  if (!stamp) throw new TypeError('buildDatasetJsonLd: dateModified is required');

  const node = {
    '@type': 'Dataset',
    '@id': `${url}#dataset`,
    name,
    description,
    url,
    creator: { '@id': creatorId },
    dateModified: stamp,
  };
  if (license) node.license = license;
  if (temporalCoverage) node.temporalCoverage = temporalCoverage;
  if (Array.isArray(keywords) && keywords.length > 0) node.keywords = keywords.slice();
  if (Array.isArray(distribution) && distribution.length > 0) {
    node.distribution = distribution.map((entry, index) => {
      requireStrings(`buildDatasetJsonLd: distribution[${index}]`, entry || {}, [
        'contentUrl',
        'encodingFormat',
      ]);
      return {
        '@type': 'DataDownload',
        contentUrl: entry.contentUrl,
        encodingFormat: entry.encodingFormat,
      };
    });
  }
  return withSchemaContext(node);
}

/** The one Offer a free public tool carries. */
const FREE_OFFER = Object.freeze({ '@type': 'Offer', price: '0', priceCurrency: DEFAULT_CURRENCY });

/**
 * WebApplication — standalone document (LINKABLE-ASSETS-001 C3): a public calculator or checker.
 * Always free (`offers` is the zero-price Offer) and `operatingSystem: 'Any'` unless told
 * otherwise; the provider is the site's Organization by `@id`.
 *
 * @param {Object} opts
 * @param {string} opts.name
 * @param {string} opts.description
 * @param {string} opts.url absolute page URL
 * @param {string} opts.applicationCategory e.g. `UtilitiesApplication`
 * @param {string} opts.providerId the site Organization's `@id`
 * @param {string} opts.dateModified a civil date or an ISO instant
 * @param {string} [opts.operatingSystem='Any']
 * @returns {Object}
 * @throws {TypeError} When a required field is missing or `dateModified` is no date.
 */
function buildWebApplicationJsonLd(opts = {}) {
  requireStrings('buildWebApplicationJsonLd', opts, [
    'name',
    'description',
    'url',
    'applicationCategory',
    'providerId',
  ]);
  const { name, description, url, applicationCategory, providerId, operatingSystem = 'Any' } = opts;
  const stamp = schemaDateOrAbsent('buildWebApplicationJsonLd', opts.dateModified);
  if (!stamp) throw new TypeError('buildWebApplicationJsonLd: dateModified is required');

  return withSchemaContext({
    '@type': 'WebApplication',
    name,
    description,
    url,
    applicationCategory,
    operatingSystem,
    offers: { ...FREE_OFFER },
    provider: { '@id': providerId },
    dateModified: stamp,
  });
}

/**
 * CollectionPage — standalone document — whose `mainEntity` is an ordered `ItemList` of the items
 * the page lists (DTOWNRETAIL-SITE-001 U6, I-16a: the tenant storefront's product list). An empty
 * list is an `ItemList` of zero, never an absent entity: a listing page with nothing on it is still
 * a listing page, and a crawler that parses the block learns that.
 *
 * @param {Object} [opts]
 * @param {string} opts.name
 * @param {string} opts.url absolute page URL
 * @param {?string} [opts.description]
 * @param {?string} [opts.inLanguage]
 * @param {Array<{name: string, url?: string}>} [opts.items] in page order, ABSOLUTE urls
 * @returns {Object}
 */
function buildCollectionPageJsonLd({ name, url, description, inLanguage, items } = {}) {
  const node = { '@type': 'CollectionPage', name, url };
  if (description) node.description = description;
  if (inLanguage) node.inLanguage = inLanguage;
  const list = Array.isArray(items) ? items : [];
  node.mainEntity = {
    '@type': 'ItemList',
    numberOfItems: list.length,
    itemListElement: list.map((entry, index) => {
      const item = { '@type': 'ListItem', position: index + 1, name: entry.name };
      if (entry.url) item.url = entry.url;
      return item;
    }),
  };
  return withSchemaContext(node);
}

/**
 * One rung of a price ladder as a `UnitPriceSpecification` (VOLUME-PRICING-001 P4): the per-unit
 * price and the quantity range it applies to (`maxValue` omitted on an open-ended top rung).
 *
 * @param {{minimumQuantity: number, maximumQuantity: ?number}} rung
 * @param {number} amount - the rung's price, already read by money (see {@link readablePriceRungs})
 * @param {string} currency
 * @returns {Object}
 */
function buildUnitPriceSpecification(rung, amount, currency) {
  const eligibleQuantity = { '@type': 'QuantitativeValue', minValue: rung.minimumQuantity };
  if (rung.maximumQuantity !== null && rung.maximumQuantity !== undefined) {
    eligibleQuantity.maxValue = rung.maximumQuantity;
  }
  return {
    '@type': 'UnitPriceSpecification',
    price: amount.toFixed(2),
    priceCurrency: currency,
    eligibleQuantity,
  };
}

/**
 * The ladder's rungs whose price can be read, each with its amount (read once by money's `readableAmount`). A rung
 * nobody can read is LEFT OUT: `Number(rung.amount).toFixed(2)` published "NaN" for one and "0.00" (a free price) for a
 * null one, in public structured data (ZERO-DEFAULT MONEY).
 *
 * @param {Array<Object>} ladder
 * @returns {Array<{rung: Object, amount: number}>}
 */
function readablePriceRungs(ladder) {
  return ladder
    .map((rung) => ({ rung, amount: readableAmount(rung.amount) }))
    .filter(({ amount }) => amount !== null);
}

/**
 * Product with an Offer — standalone document.
 *
 * @param {Object} [opts]
 * @param {string} opts.name
 * @param {string} [opts.url] absolute product URL (also the offer url)
 * @param {?string} [opts.description]
 * @param {?string} [opts.image] absolute image URL
 * @param {?string} [opts.sku]
 * @param {?(number|string)} [opts.price] DECIMAL dollars (OMNI money convention)
 * @param {string} [opts.currency='USD']
 * @param {?boolean} [opts.inStock] drives Offer.availability when a boolean
 * @param {?string} [opts.brandName]
 * @param {Array<{minimumQuantity: number, maximumQuantity: ?number, amount: number}>} [opts.ladder]
 *   the product's price ladder (VOLUME-PRICING-001 P4): with two or more rungs the Offer carries a
 *   `UnitPriceSpecification` per rung with its `eligibleQuantity` range; `price` stays rung 1
 * @returns {Object}
 */
function buildProductJsonLd({
  name,
  url,
  description,
  image,
  sku,
  price,
  currency = DEFAULT_CURRENCY,
  inStock,
  brandName,
  ladder,
} = {}) {
  const node = { '@type': 'Product', name };
  if (description) node.description = description;
  if (image) node.image = image;
  if (sku) node.sku = sku;
  if (brandName) node.brand = { '@type': 'Brand', name: brandName };

  // Offer — only when the price can be read: money's ONE reader (MONEY-ITEMS-001 U6). A price nobody can read —
  // none, '', whitespace, `false` — publishes no Offer, never one at "0.00" (`Number('   ')` is 0).
  const numericPrice = readableAmount(price);
  if (numericPrice !== null) {
    const offer = { '@type': 'Offer', priceCurrency: currency, price: numericPrice.toFixed(2) };
    if (url) offer.url = url;
    if (typeof inStock === 'boolean') offer.availability = inStock ? IN_STOCK : OUT_OF_STOCK;
    const rungs = Array.isArray(ladder) ? readablePriceRungs(ladder) : [];
    if (rungs.length >= 2) {
      offer.priceSpecification = rungs.map(({ rung, amount }) =>
        buildUnitPriceSpecification(rung, amount, currency),
      );
    }
    node.offers = offer;
  }

  return withSchemaContext(node);
}

/**
 * BreadcrumbList — standalone document.
 *
 * @param {Object} [opts]
 * @param {Array<{name: string, url?: string}>} [opts.crumbs] ordered root → current, ABSOLUTE urls
 * @returns {?Object} null when there are no crumbs
 */
function buildBreadcrumbJsonLd({ crumbs } = {}) {
  if (!Array.isArray(crumbs) || crumbs.length === 0) {
    return null;
  }
  return withSchemaContext({
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => {
      const item = { '@type': 'ListItem', position: index + 1, name: crumb.name };
      if (crumb.url) item.item = crumb.url;
      return item;
    }),
  });
}

// ── LocalBusiness (DTOWNRETAIL-SITE-001 U3) ──────────────────────────────────────────────────────
//
// A tenant site's store page describes ONE physical place: its postal address, its coordinates, its
// opening hours (the weekly windows and the dated exceptions planner's holidays make) and the
// organization it belongs to. These builders are the ONE home for that shape — marketing-shell's
// `schemaTemplates.localBusiness` delegates to them — so the LocalBusiness node a storefront emits and
// the one a marketing site emits cannot drift. Zero dependencies, by the package's rule: the schedule
// arrives as DATA in `@puffin-core/store-directory`'s shape (`buildStoreSchedule`), never through an
// import.

/** schema.org's weekday names, indexed by `location_hours.dayOfWeek` (0 = Sunday). */
const SCHEMA_DAY_OF_WEEK = Object.freeze([
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]);

/**
 * schema.org's spelling of "closed all day": `opens` and `closes` both `00:00`
 * (https://schema.org/OpeningHoursSpecification).
 */
const CLOSED_ALL_DAY = '00:00';

const TIME_SHAPE = /^(\d{2}):(\d{2})(?::\d{2})?$/;

/**
 * A `location_hours` time (`HH:mm:ss`, TS-001's wire shape) as schema.org's `HH:mm`.
 *
 * @param {string} value
 * @returns {string}
 * @throws {TypeError} When the value is not a wall-clock time.
 */
function schemaTime(value) {
  const match = TIME_SHAPE.exec(typeof value === 'string' ? value : '');
  if (!match) {
    throw new TypeError(`schemaTime: ${JSON.stringify(value)} is not a wall-clock time (HH:mm:ss)`);
  }
  return `${match[1]}:${match[2]}`;
}

/**
 * The fields of a node that carry a value — a search engine that fails to parse a block discards the
 * page's structured data entirely, so an empty string or an undefined key never leaves here.
 *
 * @param {Object} node
 * @returns {Object}
 */
function present(node) {
  const out = {};
  for (const [key, value] of Object.entries(node)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

/**
 * PostalAddress — graph member, in schema.org's own vocabulary.
 *
 * @param {Object} [opts]
 * @param {string} [opts.streetAddress]
 * @param {string} [opts.addressLocality] city
 * @param {string} [opts.addressRegion]   state / province code
 * @param {string} [opts.postalCode]
 * @param {string} [opts.addressCountry]  ISO country code
 * @returns {?Object} null when no field carries a value
 */
function postalAddressNode({
  streetAddress,
  addressLocality,
  addressRegion,
  postalCode,
  addressCountry,
} = {}) {
  const fields = present({
    streetAddress,
    addressLocality,
    addressRegion,
    postalCode,
    addressCountry,
  });
  if (Object.keys(fields).length === 0) {
    return null;
  }
  return { '@type': 'PostalAddress', ...fields };
}

/**
 * PostalAddress from a LOCATION row's columns (ADDR-001: `addressLine1`, `addressLine2`, `city`,
 * `stateCode`, `postalCode`, `countryCode`) — the public projection a tenant site reads.
 *
 * @param {Object} [location]
 * @returns {?Object}
 */
function postalAddressFromLocation(location = {}) {
  const lines = [location.addressLine1, location.addressLine2]
    .filter((line) => typeof line === 'string' && line.trim() !== '')
    .map((line) => line.trim());
  return postalAddressNode({
    streetAddress: lines.length > 0 ? lines.join(', ') : undefined,
    addressLocality: location.city,
    addressRegion: location.stateCode,
    postalCode: location.postalCode,
    addressCountry: location.countryCode,
  });
}

/**
 * GeoCoordinates — graph member. Emitted only when BOTH coordinates are finite numbers: a point with
 * one axis is not a place, and a fabricated axis is a false claim.
 *
 * @param {Object} [opts]
 * @param {number} [opts.latitude]
 * @param {number} [opts.longitude]
 * @returns {?Object}
 */
function geoCoordinatesNode({ latitude, longitude } = {}) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  return { '@type': 'GeoCoordinates', latitude, longitude };
}

/**
 * OpeningHoursSpecification nodes from a store schedule (`@puffin-core/store-directory`
 * `buildStoreSchedule`'s `{ weekly, exceptions }`).
 *
 * - Weekdays with IDENTICAL hours merge into one node whose `dayOfWeek` lists them (a crawler reads
 *   "Monday–Friday 09:00–18:00" as one spec); closed weekdays merge the same way with `00:00`–`00:00`;
 *   a weekday whose hours are UNKNOWN is omitted — never guessed.
 * - A dated exception is a node bounded by `validFrom` / `validThrough` on that date: a closed day
 *   `00:00`–`00:00`, special hours its own window. It carries no `dayOfWeek`.
 *
 * @param {Object} [schedule]
 * @param {Array<{dayOfWeek: number, status: string, openTime: ?string, closeTime: ?string}>} [schedule.weekly]
 * @param {Array<{date: string, kind: string, openTime: ?string, closeTime: ?string}>} [schedule.exceptions]
 * @returns {Array<Object>} empty when nothing is known
 */
function openingHoursSpecificationNodes({ weekly, exceptions } = {}) {
  const groups = new Map();
  for (const entry of Array.isArray(weekly) ? weekly : []) {
    let opens;
    let closes;
    if (entry.status === 'open') {
      opens = schemaTime(entry.openTime);
      closes = schemaTime(entry.closeTime);
    } else if (entry.status === 'closed') {
      opens = CLOSED_ALL_DAY;
      closes = CLOSED_ALL_DAY;
    } else {
      continue;
    }
    const key = `${opens}|${closes}`;
    if (!groups.has(key)) groups.set(key, { opens, closes, days: [] });
    groups.get(key).days.push(SCHEMA_DAY_OF_WEEK[entry.dayOfWeek]);
  }
  const nodes = [...groups.values()].map(({ opens, closes, days }) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: days,
    opens,
    closes,
  }));
  for (const exception of Array.isArray(exceptions) ? exceptions : []) {
    const closed = exception.kind === 'closed';
    nodes.push({
      '@type': 'OpeningHoursSpecification',
      validFrom: exception.date,
      validThrough: exception.date,
      opens: closed ? CLOSED_ALL_DAY : schemaTime(exception.openTime),
      closes: closed ? CLOSED_ALL_DAY : schemaTime(exception.closeTime),
    });
  }
  return nodes;
}

/**
 * The `@id` a store carries in a site's graph: `${origin}/#location-${slug}` — the same fragment
 * convention as `${origin}/#organization`.
 *
 * @param {?string} origin
 * @param {string} slug
 * @returns {string}
 */
function localBusinessId(origin, slug) {
  if (typeof slug !== 'string' || slug === '') {
    throw new TypeError('localBusinessId: slug (string) is required');
  }
  return `${absoluteUrl(origin, '/')}#location-${slug}`;
}

/**
 * LocalBusiness — graph member (no `@context`).
 *
 * Never a `Person`: like the Organization node it belongs to, it has no field that could name one.
 *
 * @param {Object} opts
 * @param {string} [opts.id]                  stable `@id` (see {@link localBusinessId})
 * @param {string} opts.name
 * @param {string} [opts.url]                 absolute page URL
 * @param {string} [opts.description]
 * @param {string} [opts.telephone]           E.164
 * @param {string} [opts.email]
 * @param {?Object} [opts.address]            a {@link postalAddressNode}
 * @param {?Object} [opts.geo]                a {@link geoCoordinatesNode}
 * @param {Array<Object>} [opts.openingHours] {@link openingHoursSpecificationNodes}; emitted only when non-empty
 * @param {Object} [opts.parentOrganization]  `{ '@id' }` of the site's Organization, or an Organization node
 * @param {string[]} [opts.sameAs]            public profile URLs (the Google Business Profile); copied, never aliased
 * @param {string} [opts.image]               absolute image URL
 * @returns {Object}
 * @throws {TypeError} When `name` is missing.
 */
function localBusinessNode({
  id,
  name,
  url,
  description,
  telephone,
  email,
  address,
  geo,
  openingHours,
  parentOrganization,
  sameAs,
  image,
} = {}) {
  if (typeof name !== 'string' || name === '') {
    throw new TypeError('localBusinessNode: name (string) is required');
  }
  const node = { '@type': 'LocalBusiness' };
  if (id) node['@id'] = id;
  node.name = name;
  Object.assign(node, present({ url, description, telephone, email, image }));
  if (address) node.address = address;
  if (geo) node.geo = geo;
  if (Array.isArray(openingHours) && openingHours.length > 0) {
    node.openingHoursSpecification = openingHours.slice();
  }
  if (parentOrganization) node.parentOrganization = parentOrganization;
  if (Array.isArray(sameAs) && sameAs.length > 0) node.sameAs = sameAs.slice();
  return node;
}

/**
 * LocalBusiness — standalone document for a tenant site's store page.
 *
 * @param {Object} opts
 * @param {?string} opts.origin            the site's origin, e.g. `https://dtownretail.com`
 * @param {string} opts.slug               the store's slug (its `@id` fragment)
 * @param {string} opts.path               the store page's path, e.g. `/shoppes/downtown`
 * @param {Object} opts.store              the public location (name, address columns, phone, email, latitude, longitude)
 * @param {Object} [opts.schedule]         `buildStoreSchedule`'s shape
 * @param {string} [opts.organizationId]   the site Organization's `@id`; default `${origin}/#organization`
 * @param {string[]} [opts.sameAs]
 * @param {string} [opts.image]            resolved through {@link resolveAssetUrl}
 * @param {string} [opts.description]
 * @returns {Object}
 */
function buildLocalBusinessJsonLd({
  origin,
  slug,
  path,
  store,
  schedule,
  organizationId,
  sameAs,
  image,
  description,
} = {}) {
  if (!store || typeof store !== 'object') {
    throw new TypeError('buildLocalBusinessJsonLd: store (object) is required');
  }
  return withSchemaContext(
    localBusinessNode({
      id: localBusinessId(origin, slug),
      name: store.name,
      url: absoluteUrl(origin, path),
      description,
      telephone: store.phone,
      email: store.email,
      address: postalAddressFromLocation(store),
      geo: geoCoordinatesNode(store),
      openingHours: openingHoursSpecificationNodes(schedule),
      parentOrganization: { '@id': organizationId || `${absoluteUrl(origin, '/')}#organization` },
      sameAs,
      image: resolveAssetUrl(origin, image),
    }),
  );
}

module.exports = {
  SCHEMA_CONTEXT,
  absoluteUrl,
  resolveAssetUrl,
  withSchemaContext,
  organizationNode,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  buildWebPageJsonLd,
  buildCollectionPageJsonLd,
  buildProductJsonLd,
  buildBreadcrumbJsonLd,
  // LINKABLE-ASSETS-001 C3 — the public-tools nodes, and the date rule they share.
  buildDatasetJsonLd,
  buildWebApplicationJsonLd,
  isSchemaDate,
  // DTOWNRETAIL-SITE-001 U3 — the LocalBusiness shape, declared once.
  SCHEMA_DAY_OF_WEEK,
  schemaTime,
  postalAddressNode,
  postalAddressFromLocation,
  geoCoordinatesNode,
  openingHoursSpecificationNodes,
  localBusinessId,
  localBusinessNode,
  buildLocalBusinessJsonLd,
};
